import { useEffect, useRef, useState } from 'react';
import { checkTwinAvailability } from '@repo/front-end-services';

import Config from '../../common/Config';
import type { CheckState } from './twinCreateForm';

/** How long typing must pause before the server is asked (spec R4.1). */
export const AVAILABILITY_DEBOUNCE_MS = 300;

interface Query {
  /** Resolves the bearer token; a rejection reads as *couldn't check*. */
  getToken: () => Promise<string>;
  organisationId: number;
  /** The WHOLE BID, prefix included — or `''` while only the prefix is there. */
  bid: string;
  name: string;
  /**
   * The BID of the twin being renamed (`twin-details-page-summary`, R3.3), so its own name is never
   * reported as taken. The create form passes none.
   */
  exceptBid?: string;
}

export interface TwinAvailabilityState {
  bid: CheckState;
  name: CheckState;
}

const IDLE: TwinAvailabilityState = { bid: 'idle', name: 'idle' };

/**
 * The create form's as-you-type check (`twin-details-page-create`, spec R4.1): one
 * `GET /api/twins/availability` per pause in typing, asking only the fields that are not empty.
 *
 * **The newest answer wins, and only for the values still in the fields.** Each check gets its own
 * `AbortController`, and a newer one aborts it — but an abort can lose the race with its own
 * response, so it only saves the network. What decides is the sequence number: an answer is applied
 * only when its check is the latest one (`A`, then `AB`, then back to `A` leaves the FIRST check
 * asking about the value now in the field, and only the sequence tells its answer is old), and only
 * when the values it asked about are still the ones in the fields.
 *
 * The call is bounded by the service (`MIDDLEWARE_REQUEST_TIMEOUT_MS`, a signal and axios's own
 * timeout) and never retried: the next keystroke is the retry. A failure, a timeout or a field the
 * server left out of its answer is `{ answer: undefined }` — *couldn't check*, never *Available*.
 */
export function useTwinAvailability({
  getToken,
  organisationId,
  bid,
  name,
  exceptBid,
}: Query): TwinAvailabilityState {
  const [state, setState] = useState<TwinAvailabilityState>(IDLE);
  const seqRef = useRef(0);
  /** The values the state on screen describes. */
  const shownForRef = useRef({ bid: '', name: '' });
  const currentRef = useRef({ bid, name });
  currentRef.current = { bid, name };
  // The token getter is a dependency of nothing: a new identity each render must not re-check.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    const seq = ++seqRef.current;
    const asked = { bid, name };
    // A field whose value did not change keeps what it shows: typing a name must not send the BID's
    // *Available* back to *Checking…*. Only an empty field is idle; only a changed one is pending.
    const before = shownForRef.current;
    shownForRef.current = asked;
    setState(prev => ({
      bid: !bid ? 'idle' : bid === before.bid && prev.bid !== 'idle' ? prev.bid : 'pending',
      name: !name ? 'idle' : name === before.name && prev.name !== 'idle' ? prev.name : 'pending',
    }));
    // Nothing to ask: show nothing, and let any check in flight die with its controller.
    if (!bid && !name) return undefined;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      void (async () => {
        let token: string;
        try {
          token = await getTokenRef.current();
        } catch {
          token = '';
        }
        const answer = token
          ? await checkTwinAvailability(
              Config.middlewareApi,
              token,
              {
                organisationId,
                bid: asked.bid || undefined,
                name: asked.name || undefined,
                ...(exceptBid ? { exceptBid } : {}),
              },
              { signal: controller.signal },
            )
          : undefined;
        // Overtaken — by typing, or by unmounting (the cleanup advances the sequence too). An
        // overtaken check is neither an error nor worth a word, however it ended.
        if (seq !== seqRef.current) return;
        const now = currentRef.current;
        if (now.bid !== asked.bid || now.name !== asked.name) return;
        if (answer === undefined) {
          // eslint-disable-next-line no-console
          // The status is the service's own line (it logs it, never the error object); this says
          // the field fell to *couldn't check*, and whether the cause was the token or the request.
          console.warn('[onboarding-create] availability unchecked', {
            cause: token ? 'request' : 'token',
          });
        }
        setState({
          bid: asked.bid ? { answer: answer?.bid } : 'idle',
          name: asked.name ? { answer: answer?.name } : 'idle',
        });
      })();
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
      seqRef.current += 1;
    };
  }, [organisationId, bid, name, exceptBid]);

  return state;
}
